import axios, { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import Cookies from "js-cookie";
import { keysToCamel, keysToSnakeCase } from "./utils";
import { ErrorAnalytics } from './analytics';

let isRefreshing = false;
let failedQueue: any[] = [];

const APIEndpoint: string = process.env.NODE_ENV === 'production' ? `https://${process.env.REACT_APP_HOST}/api` : 'http://localhost:3000/api'
const APIClient = axios.create({ baseURL: APIEndpoint, withCredentials: true });

/**
 * Processes the failed queue by resolving or rejecting promises based on the error state.
 */
const processQueue = (error: any = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

/**
 * Module-level token refresh used by the response interceptor. Interceptors
 * are plain async callbacks — NOT React components or hooks — so this must
 * never call useAuth()/useContext (Rules of Hooks). Reads and rotates the
 * mindara_at/mindara_rt cookies directly, mirroring the cookie handling in
 * src/hooks/AuthProvider.tsx.
 */
export async function refreshAccessToken(): Promise<string> {
  const refreshToken = Cookies.get("mindara_rt");
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  const response = await APIClient.post(
    "/token/refresh/",
    { refresh: refreshToken },
    { _isTokenRefreshCall: true } as any
  );
  const { access, refresh } = response.data;
  if (!access || !refresh) {
    throw new Error("Refresh response missing tokens");
  }

  Cookies.set("mindara_at", access, { sameSite: "lax", expires: 1 });
  Cookies.set("mindara_rt", refresh, { sameSite: "lax", expires: 7 });

  return access;
}

APIClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add authentication token
    const token = Cookies.get("mindara_at");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Handle case conversion for request data
    const method = config.method?.toLowerCase();

    if (['post', 'patch', 'delete'].includes(method || '')) {
      if (config.data && typeof config.data === 'object') {
        config.data = keysToSnakeCase(config.data);
      }
    } else if (method === "get") {
      if (config.params && typeof config.params === 'object') {
        config.params = keysToSnakeCase(config.params);
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

APIClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean; _isTokenRefreshCall?: boolean };

    if (originalRequest._isTokenRefreshCall) {
      return Promise.reject(error);
    }

    if ([401, 403].includes(error.response?.status) && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return APIClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Call the refresh token endpoint directly
        const access = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${access}`;
        processQueue();
        return APIClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error);
        console.error("Refresh token failed, user needs to log in again.", refreshError);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Track API errors for analytics
    if (error.response) {
      const endpoint = error.config?.url || 'unknown';
      const statusCode = error.response.status;
      const errorMessage = error.response.data?.detail || error.response.statusText;

      ErrorAnalytics.apiError(endpoint, statusCode, errorMessage);
    }

    return Promise.reject(error);
  }
);

// Convert snake_case keys (typical for Django model fields returned from the API) to camelCase
APIClient.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    if (response.data && typeof response.data === 'object') {
      response.data = keysToCamel(response.data);
    }
    return response;
  },
  error => {
    return Promise.reject(error);
  }
);

export { APIClient, APIEndpoint };
